import PageHero from "@/components/PageHero";
import useSEO from "@/hooks/useSEO";
import Reveal from "@/components/Reveal";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Megaphone, Search, Share2, FileText, Palette, Video,
  Code2, Workflow, BarChart3, Magnet, ArrowUpRight
} from "lucide-react";

const SECTIONS = [
  {
    eyebrow: "Performance Marketing",
    icon: Megaphone,
    color: "saavi-blue",
    title: "Generate qualified leads and sales",
    desc: "Through highly optimized campaigns across every paid channel.",
    items: ["Google Ads", "Meta Ads", "LinkedIn Ads", "YouTube Advertising", "Remarketing Campaigns", "Performance Max"],
    img: "/services/performance-marketing.jpg",
    tag: "Paid Acquisition",
  },
  {
    eyebrow: "SEO",
    icon: Search,
    color: "saavi-green",
    title: "Be visible where customers are searching",
    desc: "From technical foundations to AI-search readiness — full-stack SEO that ranks.",
    items: ["Technical SEO", "On-Page SEO", "Local SEO", "AI Search Optimization", "Content SEO", "Link Building"],
    img: "/services/seo.jpg",
    tag: "Organic Discovery",
  },
  {
    eyebrow: "Social Media Marketing",
    icon: Share2,
    color: "saavi-red",
    title: "Build community & customer engagement",
    desc: "Brand-building across every channel your customers live on.",
    items: ["Instagram", "Facebook", "LinkedIn", "YouTube", "X (Twitter)", "Threads"],
    img: "/services/social-media-marketing.jpg",
    tag: "Audience & Brand",
  },
  {
    eyebrow: "Content Marketing",
    icon: FileText,
    color: "saavi-blue",
    title: "Content that converts",
    desc: "We create content that educates, engages, and drives action.",
    items: ["Blogs", "Articles", "Website Content", "Email Campaigns", "Landing Page Copy", "Case Studies"],
    img: "/services/content-marketing.jpg",
    tag: "Storytelling",
  },
  {
    eyebrow: "Creative & Design",
    icon: Palette,
    color: "saavi-red",
    title: "Design with intent — and outcomes",
    desc: "Brand-consistent creative across every touchpoint.",
    items: ["Social Media Creatives", "Brand Identity", "Corporate Presentations", "Brochures", "Infographics", "Digital Ads", "Event Branding"],
    img: "/services/creative-design.jpg",
    tag: "Visual Identity",
  },
  {
    eyebrow: "Video Production",
    icon: Video,
    color: "saavi-green",
    title: "Video that moves audiences",
    desc: "From cinematic brand films to short-form social reels.",
    items: ["Brand Films", "Corporate Videos", "Product Videos", "Motion Graphics", "Reels", "Animation", "Testimonials"],
    img: "/services/video-production.jpg",
    tag: "Motion & Film",
  },
  {
    eyebrow: "Website & Development",
    icon: Code2,
    color: "saavi-blue",
    title: "Build digital experiences that convert",
    desc: "Fast, conversion-focused websites built for growth.",
    items: ["Corporate Websites", "Landing Pages", "E-Commerce", "WordPress Development", "UI/UX Design"],
    img: "/services/website-development.jpg",
    tag: "Web & Product",
  },
  {
    eyebrow: "Marketing Automation",
    icon: Workflow,
    color: "saavi-red",
    title: "Turn your marketing into a system",
    desc: "Automated funnels & CRM that nurture every lead to revenue.",
    items: ["Email Automation", "WhatsApp Automation", "CRM Integration", "Lead Nurturing", "Funnel Creation", "Conversion Tracking"],
    img: "/services/marketing-automation.jpg",
    tag: "Funnels & CRM",
  },
  {
    eyebrow: "Analytics & Reporting",
    icon: BarChart3,
    color: "saavi-green",
    title: "Measure what matters",
    desc: "Custom dashboards & attribution that show real ROI.",
    items: ["Google Analytics Setup", "Dashboard Creation", "Attribution Tracking", "Campaign Reporting"],
    img: "/services/analytics-reporting.jpg",
    tag: "Data & Insights",
  },
  {
    eyebrow: "Lead Generation",
    icon: Magnet,
    color: "saavi-blue",
    title: "Turn clicks into customers",
    desc: "Our lead-gen framework helps businesses generate predictable revenue growth.",
    items: ["Google Search", "Meta Advertising", "LinkedIn Campaigns", "Display Advertising", "YouTube Advertising", "Education Portals", "Native Ads"],
    img: "/services/lead-generation.jpg",
    tag: "Pipeline Engine",
  },
];

export default function Services() {
  useSEO();
  return (
    <>
      <PageHero
        eyebrow="Services"
        title={<>A complete growth stack —<br /><span className="text-saavi-blue">paid, organic, content & tech.</span></>}
        description="From Performance Marketing and SEO to Content, Creative, Web, Automation and Lead Generation — every service is designed to compound your growth."
      />

      <section className="border-b border-border">
        <div className="container-saavi divide-y divide-border">
          {SECTIONS.map(({ eyebrow, icon: Icon, color, title, desc, items, img, tag }, idx) => {
            const reverse = idx % 2 === 1;
            return (
              <div key={eyebrow} className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 py-16 md:py-24">
                {/* VISUAL */}
                <Reveal
                  variant={reverse ? "slide-right" : "slide-left"}
                  className={`lg:col-span-5 ${reverse ? "lg:order-2" : ""}`}
                >
                  <div
                    data-testid={`service-visual-${eyebrow.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`relative ai-card group overflow-hidden rounded-md border border-border bg-card h-full min-h-[280px] hover:border-${color}`}
                  >
                    <img
                      src={img}
                      alt={eyebrow}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
                    />
                    {/* Subtle color tint that fades on hover — kept light so AI imagery stays vivid */}
                    <div
                      aria-hidden="true"
                      className={`absolute inset-0 bg-gradient-to-br from-${color}/20 via-transparent to-transparent mix-blend-multiply dark:mix-blend-screen opacity-80 transition-opacity duration-500 group-hover:opacity-40`}
                    />
                    {/* Bottom dark gradient for legibility of the label */}
                    <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />

                    {/* Corner brackets */}
                    <span className="absolute top-3 left-3 w-4 h-4 border-l-2 border-t-2 border-white/70" aria-hidden="true" />
                    <span className="absolute top-3 right-3 w-4 h-4 border-r-2 border-t-2 border-white/70" aria-hidden="true" />
                    <span className="absolute bottom-3 left-3 w-4 h-4 border-l-2 border-b-2 border-white/70" aria-hidden="true" />
                    <span className="absolute bottom-3 right-3 w-4 h-4 border-r-2 border-b-2 border-white/70" aria-hidden="true" />

                    {/* Top-left chip */}
                    <div className="absolute top-5 left-5 flex items-center gap-2 ai-pill px-3 py-1.5 rounded-md text-white/95">
                      <Icon className="h-3.5 w-3.5" />
                      <span className="font-mono-overline">{tag}</span>
                    </div>

                    {/* Top-right index */}
                    <div className="absolute top-5 right-5 font-mono-overline text-white/80 text-[10px]">
                      0{idx + 1} / {SECTIONS.length}
                    </div>

                    {/* Bottom-left big label */}
                    <div className="absolute bottom-5 left-5 right-5">
                      <div className="font-mono-overline text-white/70 mb-1">Service · 0{idx + 1}</div>
                      <h3 className="text-2xl md:text-3xl text-white font-extrabold tracking-tight leading-tight">{eyebrow}</h3>
                    </div>
                  </div>
                </Reveal>

                {/* CONTENT */}
                <div className={`lg:col-span-7 ${reverse ? "lg:order-1" : ""}`}>
                  <Reveal variant="fade-up">
                    <div className="flex items-center gap-3 mb-5">
                      <div className={`inline-flex h-10 w-10 items-center justify-center rounded-md border border-border text-${color}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <span className={`font-mono-overline text-${color}`}>0{idx + 1} · {eyebrow}</span>
                    </div>
                    <h2 className="text-3xl md:text-4xl">{title}</h2>
                    <p className="mt-4 text-muted-foreground leading-relaxed max-w-xl">{desc}</p>
                  </Reveal>

                  <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-3">
                    {items.map((it, i) => (
                      <Reveal key={it} variant="fade-up" delay={i * 60}>
                        <div className={`card-hover lift-on-hover ai-card border border-border rounded-md px-4 py-4 bg-card text-sm font-semibold hover:border-${color} h-full`}>
                          {it}
                        </div>
                      </Reveal>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="section-pad">
        <div className="container-saavi flex flex-col md:flex-row items-start md:items-center justify-between gap-6 border border-border rounded-md p-10 bg-card">
          <div>
            <h3 className="text-2xl md:text-3xl">Not sure which service fits?</h3>
            <p className="mt-2 text-muted-foreground">Get a free marketing audit — we'll show you where to invest.</p>
          </div>
          <Link to="/contact"><Button size="lg" className="h-12 px-6 gap-2">Get Free Audit <ArrowUpRight className="h-4 w-4" /></Button></Link>
        </div>
      </section>
    </>
  );
}
